import React from 'react'
import { FormProvider } from 'react-hook-form'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { IUpdateTransfixHighwayInsurancePolicyFlagInput } from 'types/graphqlTypes'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui'
import { useUpdateTransfixHighwayInsurancePolicyFlagMutation } from '../../graphql/UpdateTransfixHighwayInsurancePolicyFlagMutation'
import { TransfixFlagEditModalForm } from './TransfixFlagEditModalForm'
import { TTransfixFlagEditModalSchema } from './TransfixFlagEditModalSchema'
import { useTransfixFlagEditModalForm } from './useTransfixFlagEditModalForm'

interface ITransfixFlagEditModalProps extends Omit<IModalBaseProps, 'modalType'> {
  highwayInsurancePolicyId: IUpdateTransfixHighwayInsurancePolicyFlagInput['highwayInsurancePolicyId']
  isType: string
  transfixAdditionalInsured: TTransfixFlagEditModalSchema['transfixAdditionalInsured']
  transfixLossPayee: TTransfixFlagEditModalSchema['transfixLossPayee']
  transfixHolder: TTransfixFlagEditModalSchema['transfixHolder']
}

export const TransfixFlagEditModal = ({
  highwayInsurancePolicyId,
  isType,
  onClose,
  open = false,
  transfixAdditionalInsured,
  transfixHolder,
  transfixLossPayee,
  ...props
}: ITransfixFlagEditModalProps) => {
  const methods = useTransfixFlagEditModalForm({
    defaultValues: {
      transfixAdditionalInsured,
      transfixLossPayee,
      transfixHolder,
    },
  })

  const { handleSubmit } = methods || {}

  const [
    updateTransfixHighwayInsurancePolicyFlagMutation,
    { data, loading },
  ] = useUpdateTransfixHighwayInsurancePolicyFlagMutation({
    variables: {
      input: {
        highwayInsurancePolicyId,
      },
    },
  })

  const { updateTransfixHighwayInsurancePolicyFlag } = data || {}
  const { errors } = updateTransfixHighwayInsurancePolicyFlag || {}

  const cancelHandler = () => {
    onClose()
  }
  const saveHandler = handleSubmit((data: TTransfixFlagEditModalSchema) => {
    updateTransfixHighwayInsurancePolicyFlagMutation({
      variables: {
        input: {
          highwayInsurancePolicyId,
          ...data,
        },
      },
    }).then(() => {
      onClose()
    })
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: cancelHandler,
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: saveHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
      disabled: loading,
    },
  ]

  return (
    <ModalBase
      dataTest='TransfixFlagEditModal'
      modalType={MODAL_TYPES.form}
      size={8}
      open={open}
      actions={actions}
      onClose={onClose}
      title='Edit Insurance Information'
      {...props}>
      <FormProvider {...methods}>
        {errors && (
          <InlineMessage
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            message={errors.reduce((acc, { message }) => `${acc} ${message}`, '')}
          />
        )}
        <TransfixFlagEditModalForm isType={isType} />
      </FormProvider>
    </ModalBase>
  )
}
